import { useEffect } from 'react'
import classNames from 'classnames'
import { withRouter } from 'i18n/utils'

import { CardSection } from 'shared/components/CardSection'
import ErrorMessage from 'shared/components/ErrorMessage'
import Button from 'shared/components/Button'
import CircleLoader from '@components/CircleLoader'

import withRates from 'shared/hocs/withRates'
import useRequestPending from 'shared/hooks/useRequestPending'
import { setLastBookingDetails, fetchEventRate } from '@booking/actions'
import { eventRatePendingSelector, waitingListKeySelector } from '@booking/selectors'

import { selectedEventSelector, eventsListSelector, eventsSearchPendingSelector } from 'search/selectors'
import { fetchEvents, setEvent, removeEvent } from 'search/actions'

import DateSelection from './DateSelection'
import EventProduct from './EventProduct'

import { sendAmplitudeEvent, useSendGTAGEvent } from 'analytics/utils'
import { ANALYTICS_FLOW_TYPE_EVENT } from 'analytics/constants'

import styles from 'booking/components/flow-steps/styles.module.scss'
import eventStyles from '../styles.module.scss'

const DateProductSelection = ({
    grsId,
    setDateRange,
    onNext,
    setProduct,
    fetchEvents,
    setEvent,
    eventsList,
    eventsSearchPending,
    bookingData,
    selectedEvent,
    setLastBookingDetails,
    currentItemIndex,
    waitingListKey,
    removeEvent,
    fetchEventRate,
    eventRatePending,
}) => {
    const sendGTAGEvent = useSendGTAGEvent()
    useEffect(() => {
        removeEvent()
        fetchEvents({ locationId: grsId })
    }, [])

    useEffect(() => {
        if (selectedEvent) {
            setDateRange({
                startDate: selectedEvent.fromDate,
                endDate: selectedEvent.toDate,
                displayValue: `${selectedEvent.fromDate.format('DD-MM-YYYY')} — ${selectedEvent.toDate.format(
                    'DD-MM-YYYY'
                )}`,
            })
        }
    }, [selectedEvent])

    const proceedToNextStep = () => {
        setProduct({ ...selectedEvent, displayValue: selectedEvent.eventName })
        sendAmplitudeEvent('Selected Product', {
            'Product Name': selectedEvent?.rateName,
            'Product ID': selectedEvent?.rateId,
            Price: selectedEvent.amount,
            'Flow Type': ANALYTICS_FLOW_TYPE_EVENT,
        })
        sendGTAGEvent('select_product', {
            flow_type: ANALYTICS_FLOW_TYPE_EVENT,
            product_name: selectedEvent?.rateName,
            product_id: selectedEvent?.rateId,
            price: selectedEvent.amount,
        })
        onNext()
    }

    // On Next: load the event price breakdown while showing a loading
    // animation, then continue once the rate data has been fetched
    useRequestPending(eventRatePending, proceedToNextStep, 'eventRate')

    const onNextStep = () => {
        fetchEventRate({
            locationId: selectedEvent.locationId,
            rateId: selectedEvent.rateId,
            eventId: selectedEvent.eventId,
        })
    }

    const onDateInputChange = (startDate, endDate) => {
        if (!startDate && !endDate) {
            setDateRange(null)
            removeEvent()
            fetchEvents({ locationId: grsId })
        } else {
            setDateRange({
                startDate: startDate,
                endDate: endDate,
                displayValue: `${startDate.format('DD-MM-YYYY')} — ${endDate.format('DD-MM-YYYY')}`,
            })

            fetchEvents({
                locationId: grsId,
                startDate: startDate,
                endDate: endDate,
            })
        }
    }

    useEffect(() => {
        setLastBookingDetails({
            id: currentItemIndex,
            grsId,
            selectedEvent,
            waitingListKey,
            rate: selectedEvent,
        })
    }, [selectedEvent])

    return (
        <>
            <CardSection className={styles.contentSection} loading={false}>
                <h3 className={eventStyles.headlineMobileVersion}>
                    <T>booking.event.parking</T>
                </h3>
                <DateSelection onDateInputChange={onDateInputChange} />
                {!eventsSearchPending && eventsList.length === 0 && (
                    <ErrorMessage messages={[<T>booking.event.no.results</T>]} />
                )}
                {eventsSearchPending ? (
                    <div className="row justify-content-center m-6">
                        <CircleLoader />
                    </div>
                ) : (
                    eventsList.length > 0 &&
                    eventsList.map((event, i) => (
                        <EventProduct
                            key={i}
                            event={event}
                            date={bookingData.date}
                            setActiveProduct={setEvent}
                            selectedEvent={selectedEvent}
                        />
                    ))
                )}
                <div className={classNames('row', styles.nextButtonContainer)}>
                    <Button
                        btnStyle="primary"
                        onClick={onNextStep}
                        disabled={!selectedEvent || eventsSearchPending || eventRatePending}
                        className={styles.nextButton}>
                        {eventRatePending ? (
                            <CircleLoader size="small" color="light" />
                        ) : (
                            <T>subscription.purchase.label.next</T>
                        )}
                    </Button>
                </div>
            </CardSection>
        </>
    )
}

export default compose(
    withRouter,
    connect(
        createStructuredSelector({
            selectedEvent: selectedEventSelector,
            eventsList: eventsListSelector,
            eventsSearchPending: eventsSearchPendingSelector,
            eventRatePending: eventRatePendingSelector,
            waitingListKey: waitingListKeySelector,
        }),
        {
            removeEvent,
            setEvent,
            fetchEvents,
            fetchEventRate,
            setLastBookingDetails,
        }
    )
)(withRates(DateProductSelection))
